import React from 'react'
import {
  Root,
  Description,
  Action,
  Close,
  Viewport,
} from '@radix-ui/react-toast'
import { IconSize } from '@sidecar-components/atoms/Icon'
import { Button, ButtonSentiment } from '@sidecar-components/atoms/Button'
import { IconButton } from '@sidecar-components/molecules/IconButton'
import { Sentiment } from '@sidecar-components/types'

import {
  actionStyles,
  containerStyles,
  descriptionStyles,
  dismissStyles,
  dismissIconStyles,
  viewportStyles,
} from './styles'
import { ToastProps, ToastType } from './Toast.types'

/**
 * The Toast(er) component is a component that is expected to be used in one
 * part, the root, of the app. It accepts an array of toast configs that are
 * then used to create each toast according to however RadixUI displays them.
 *
 * It should only be in one location per app because it includes a Viewport from
 * where the toasts are rendered and this shouldn't move or be duplicated.
 *
 * Called a "toast(er)" be cause it's named Toast, but it does act more like a
 * real toaster in that all the toasts pop up from the same place.
 *
 * @param {array} toasts The array of toasts to display
 * @returns A Toast(er) component to place in the App root for showing messages
 */
export const Toast = ({ toasts = [] }) => (
  <>
    {toasts.map(
      (
        {
          action,
          actionAltTag,
          actionLabel,
          sentiment = Sentiment.ACCENT,
          type = ToastType.FOREGROUND,
          open,
          onOpenChange,
          content,
          className,
          duration,
          id,
        }: ToastProps,
        index,
      ) => {
        // Convert Sentiment to ButtonSentiment so the Action button can use it
        let btnSentiment
        switch (sentiment) {
          case Sentiment.ACCENT:
            btnSentiment = ButtonSentiment.ACCENT
            break

          case Sentiment.RISK:
            btnSentiment = ButtonSentiment.RISK
            break

          default:
            btnSentiment = ButtonSentiment.ACCENT
            break
        }

        return (
          <Root
            // Keyed by the toast's own id so React preserves each toast's
            // identity (and Radix's open/close transition state) across
            // queue changes. Falls back to index for callers that don't
            // pass an id, matching the prior behavior.
            key={id ?? `toast_${index}`}
            type={type}
            open={open}
            onOpenChange={onOpenChange}
            duration={duration}
            className={`${containerStyles(sentiment)} ${className}`}
          >
            {/* Always show a description */}
            <Description className={descriptionStyles(sentiment)}>
              {content}
            </Description>

            {/* Add an action if there is one */}
            {action && (
              <Action altText={actionAltTag} asChild className={actionStyles}>
                <Button sentiment={btnSentiment} onClick={action}>
                  {actionLabel}
                </Button>
              </Action>
            )}

            {/* Always show a dismiss x button */}
            <Close asChild aria-label="Dismiss">
              <IconButton
                icon="x"
                label="dismiss"
                size={IconSize.SM}
                sentiment={sentiment}
                className={dismissStyles(sentiment)}
                iconClass={dismissIconStyles(sentiment)}
              />
            </Close>
          </Root>
        )
      },
    )}

    {/* This is where all the toasts are popped out from */}
    <Viewport className={viewportStyles} />
  </>
)
